import { useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Grid, Stack } from '@mui/material';
import { toast } from 'react-toastify';

// components
import { useSelector } from 'react-redux';
import { isAdmin, isStakeholder } from '../../utils/share';
import { ROLE_CAN_UPDATE_TASK_CLASSIFICATION, ROLE_CAN_REMOVE_PROJECT, STAKEHOLDER_OVERVIEW_VISIBILITY } from '../../Constants/ProjectConstants';
import { AddMemberModal } from './details/components';

// services
import Loading from '../../layouts/loading';
import {
  MoreDetails,
  ProjectDescription,
  ProjectMembers,
  ProjectTaskClassification,
  ProjectLinks,
  ProjectWeeklyUpdate,
} from './overview/components';
import { useProject } from './Layout/header/context';
import { DeleteModal } from './overview/components/popups';
import { deleteProject } from '../../services/projectService';
import Iconify from '../../components/iconify/Iconify';
import ProjectRisks from './overview/components/ProjectRisks';

// ----------------------------------------------------------------------

function ProjectOverview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [openDelete, setOpenDelete] = useState(false);
  const [openAddMemberForm, setOpenAddMemberForm] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const { projectDetails, refetchProject, userIsStakeholder } = useProject();

  const handleMemberFormOpen = useCallback(() => setOpenAddMemberForm(true), []);
  const handleMemberFormClose = useCallback(() => setOpenAddMemberForm(false), []);

  const handleDeleteClick = useCallback(() => setOpenDelete(true), []);
  const handleDeleteModalClose = useCallback(() => setOpenDelete(false), []);

  const handleDelete = useCallback(async () => {
    try {
      await deleteProject(id);
      toast.success('Project has been deleted');
      navigate(`/projects`);
    } catch (error) {
      toast.error('Something went wrong while deleting the project');
    }
  }, [id, navigate]);

  const accountData = useSelector((state) => state?.login?.user || null);

  const consumedHrs = projectDetails?.projectmember?.reduce((sum, member) => sum + member?.totalTime / 3600, 0) ?? 0;

  const projectRole =
    projectDetails?.projectmember?.find((data) => data.userId === accountData?.id)?.projectRoles?.roleName ?? '';

  const isPermissionUpdateTaskClassifications = ROLE_CAN_UPDATE_TASK_CLASSIFICATION.includes(projectRole);
  const showTaskClassification = !userIsStakeholder || STAKEHOLDER_OVERVIEW_VISIBILITY.taskClassification !== false;
  const showProjectMembers = !userIsStakeholder || STAKEHOLDER_OVERVIEW_VISIBILITY.projectMembers !== false;

  return (
    <>
      <Stack spacing={3}>
        <MoreDetails onDeleteClick={handleDeleteClick} />

        {showTaskClassification && (
          <ProjectTaskClassification
            projectId={id}
            isPermissionUpdateTaskClassifications={isPermissionUpdateTaskClassifications}
          />
        )}

        {showProjectMembers && (
          <ProjectMembers
            onAddMemberClick={handleMemberFormOpen}
            setLoading={setLoading}
            userIsStakeholder={userIsStakeholder}
          />
        )}
        <ProjectDescription />
        <ProjectLinks projectId={id} />
        {!isStakeholder() && <ProjectRisks projectId={id} />}
        {isAdmin() && <ProjectWeeklyUpdate />}

        {(isAdmin() || ROLE_CAN_REMOVE_PROJECT.includes(projectRole)) && (
          <Grid item xs={12}>
            <Stack direction="row" alignItems="center" justifyContent="flex-end">
              <Button
                color="error"
                onClick={handleDeleteClick}
                startIcon={<Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }} />}
              >
                Delete Project
              </Button>
            </Stack>
          </Grid>
        )}
      </Stack>

      <AddMemberModal
        openModal={openAddMemberForm}
        onClose={handleMemberFormClose}
        action="create"
        consumedHrs={consumedHrs}
        id={id}
        projectDetails={projectDetails}
        onSubmit={refetchProject}
      />

      <DeleteModal open={openDelete} onClose={handleDeleteModalClose} onDelete={handleDelete} />
      <Loading open={isLoading} />
    </>
  );
}
export default ProjectOverview;
